import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Dropdown, { StyledDropdownIcon } from '@components/Dropdown';
import Button from '../../../Button';
import TimeFrom from '../TimeFrom/TimeFrom';
import MicCheckButton from '../../Dialer/MicCheckButton';

import { Bug } from '@icons/crm';

const { Sizes } = Button;

interface BugsDropdownProps {
  className?: string;
  onCopy?: () => void;
  onReset?: () => void;
  onToggle: (open: boolean) => void;
  onOpenReport?: () => void;
  hasSocketError?: false;
  hasVoxError?: false;
  hasUpdates?: false;
  list?: {
    title: string;
    message: string;
    description?: string;
    createdAt: number;
    url?: string;
  }[];
}

export const BugsDropdown = ({
  className,
  list = [],
  hasSocketError,
  hasVoxError,
  hasUpdates,
  onCopy,
  onReset,
  onToggle,
  onOpenReport,
}: LT.WithChildren<BugsDropdownProps>): JSX.Element => {
  const [hasNewBugs, setNewBugs] = useState(false);
  const [isConnected, setConnected] = useState(true);
  const [savedList, setList] = useState(list);

  useEffect(() => {
    if (list.length > 0 && list !== savedList) {
      setNewBugs(true);

      setTimeout(() => {
        setNewBugs(false);
        setList(list);
      }, 2000);
    }
  }, [list]);

  useEffect(() => {
    const handleOffline = () => setConnected(false);
    const handleOnline = () => setConnected(true);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const headRenderer = useMemo(
    () => (
      <StyledHead
        data-socket-error={!isConnected || hasSocketError}
        data-has-updates={hasUpdates}
      >
        <Bug width={13} />
        <StyledLabel>
          {isConnected ? list.length : 'Отсутствует интернет'}
        </StyledLabel>
      </StyledHead>
    ),
    [list, hasSocketError, hasUpdates, isConnected, hasNewBugs]
  );

  const contentRenderer = useMemo(() => {
    const isNotEmptyList = list.length > 0;
    return (
      <StyledContent>
        <>
          {!!hasSocketError && (
            <StyledSocketError>
              Ошибка автообновления данных.
              <br />
              Подождите, либо перезагрузите CRM.
            </StyledSocketError>
          )}

          {!!hasVoxError && (
            <StyledVoxError>
              Возможны сбои в работе телефонии.
              <br />
              Партнер уже работает над их устранением.
            </StyledVoxError>
          )}

          {!!hasUpdates && (
            <StyledUpdates>
              Доступны новые обновления.
              <br />
              Перезагрузите CRM.
            </StyledUpdates>
          )}

          {isNotEmptyList && (
            <StyledBugs>
              {list.map(({ title, message, description, createdAt }) => (
                <StyledBug key={`${title} + ${createdAt}`} title={message}>
                  <StyledBugMeta>
                    <StyledPriorityIcon />
                    <StyledBugTitle>{`${title}${
                      message ? ` (${message})` : ''
                    }`}</StyledBugTitle>
                    <StyledBugTime time={createdAt} />
                  </StyledBugMeta>
                  {!!description && (
                    <StyledDescription>{description}</StyledDescription>
                  )}
                </StyledBug>
              ))}
            </StyledBugs>
          )}

          <StyledButtons>
            {isNotEmptyList && onCopy && (
              <Button onClick={onCopy} size={Sizes.TINY}>
                Копировать лог
              </Button>
            )}
            {isNotEmptyList && onReset && (
              <Button onClick={onReset} size={Sizes.TINY}>
                Сбросить ошибки
              </Button>
            )}
            {onOpenReport && (
              <Button
                onClick={() => {
                  onOpenReport();
                  onToggle(false);
                }}
                size={Sizes.TINY}
              >
                Создать репорт
              </Button>
            )}
            <MicCheckButton />
          </StyledButtons>
        </>
      </StyledContent>
    );
  }, [hasSocketError, hasVoxError, hasUpdates, list]);

  return (
    <StyledBugsDropdown
      className={className}
      headRenderer={headRenderer}
      contentRenderer={contentRenderer}
      hasNewBugs={hasNewBugs}
      hasUpdates={hasUpdates}
      onToggle={onToggle}
    />
  );
};

const StyledBugsDropdown = styled(Dropdown)<{
  hasNewBugs?: boolean;
  hasUpdates?: boolean;
}>`
  background: white;
  font-size: 13px;
  position: relative;
  border: none;
  border-radius: 4px;

  ${StyledDropdownIcon} {
    display: none;
  }

  ${({ hasNewBugs }) =>
    hasNewBugs &&
    `
    box-shadow: 0 0 4px #fc788f;
    border-color: #fc788f;
  `};

  ${({ hasUpdates }) =>
    hasUpdates &&
    `
    box-shadow: 0 0 4px #78c9fc;
    border-color: #78c9fc;
  `};
`;

const StyledBug = styled.div`
  padding: 5px 0;
`;

const StyledBugMeta = styled.div`
  display: flex;
  column-gap: 5px;
  align-items: center;
  text-align: left;
`;

const StyledBugs = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 400px;

  ${StyledBug} {
    border-top: 1px solid #dadada;

    &:first-child {
      border-top: none;
    }
  }
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const StyledPriorityIcon = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 100%;
  background: #e21f4b;
`;

const StyledSocketError = styled.div`
  border: 1px solid #ffe4e4;
  border-radius: 4px;
  background: #fff0f0;
  padding: 4px 0;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StyledVoxError = styled(StyledSocketError)``;

const StyledUpdates = styled.div`
  border: 1px solid #dceeff;
  border-radius: 4px;
  background: #f0fbff;
  padding: 4px 0;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StyledBugTitle = styled.div`
  flex: 1;
  font-size: 14px;
  padding-right: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledDescription = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
  text-align: left;
  color: #656565;
`;

const StyledBugTime = styled(TimeFrom)`
  font-size: 11px;
`;

const StyledLabel = styled.span`
  position: relative;
  z-index: 3;
`;

const StyledHead = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  display: flex;
  column-gap: 5px;
  height: 40px;
  padding: 0 7px;
  border: solid 1px #c2c5cc;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  white-space: nowrap;

  > * {
    position: relative;
    z-index: 3;
  }

  &[data-socket-error='true'] {
    border: 1px solid #ffb1c3;
    background: #fff0f0;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 40px;
      height: 100%;
      background: #fff0f0;
      background: linear-gradient(90deg, #fff0f0 0%, #ffe4e4 50%, #fff0f0);
      animation: highlight-error 1.2s ease-in-out infinite;
    }
  }

  &[data-has-updates='true'] {
    border: 1px solid #92d8f0;
    background: #f0fbff;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 40px;
      height: 100%;
      background: #f0fbff;
      background: linear-gradient(90deg, #f0fbff 0%, #dceeff 50%, #f0fbff);
      animation: highlight-error 1.2s ease-in-out infinite;
    }
  }

  @keyframes highlight-error {
    0%,
    100% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
  }
`;

const StyledContent = styled.div`
  padding: 10px 15px;
  width: 400px;
`;
